import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TextField, PrimaryButton, Dropdown, IDropdownOption, Stack, Label, Spinner, SpinnerSize } from "@fluentui/react";
import { useMsal } from "@azure/msal-react";
import { getToken } from "../../authConfig";
import styles from "./ClinicalSettings.module.css";
import { useClinicalFilters } from "../../clinicalContext";
import { logDevChoice } from "../../api";
import { SHOW_SUMMARIES } from "../../featureFlags";

// Collapsible facet section with checkboxes and clear link
interface FacetSectionProps {
    title: string;
    options: { key: string | number; text: string }[];
    selectedKeys: string[];
    onChange: (newSelection: string[]) => void;
    maxVisible?: number;
}

const FacetSection: React.FC<FacetSectionProps> = ({ title, options, selectedKeys, onChange, maxVisible = 5 }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [showAll, setShowAll] = useState(false);

    const hasSelection = selectedKeys.length > 0;
    const displayOptions = showAll ? (options ?? []) : (options ?? []).slice(0, maxVisible);
    const hasMore = (options ?? []).length > maxVisible;

    const handleCheckboxChange = (key: string, checked: boolean) => {
        const newSelection = checked ? [...selectedKeys, key] : selectedKeys.filter(k => k !== key);
        console.log("FacetSection handleCheckboxChange:", { key, checked, newSelection });
        onChange(newSelection);
    };

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        onChange([]);
    };

    return (
        <div className={styles.facetSection}>
            <div className={styles.facetHeader} onClick={() => setIsExpanded(!isExpanded)}>
                <span className={styles.facetTitle}>{title}</span>
                <span className={styles.facetChevron}>{isExpanded ? "▼" : "▶"}</span>
            </div>
            {isExpanded && (
                <div className={styles.facetBody}>
                    {hasSelection && (
                        <button className={styles.clearLink} onClick={handleClear}>
                            &lt; Clear
                        </button>
                    )}
                    {displayOptions.map(opt => (
                        <label key={opt.key} className={styles.facetCheckbox}>
                            <input
                                type="checkbox"
                                checked={selectedKeys.includes(opt.key as string)}
                                onChange={e => handleCheckboxChange(opt.key as string, e.target.checked)}
                            />
                            <span>{opt.text}</span>
                        </label>
                    ))}
                    {hasMore && (
                        <button className={styles.seeMoreLink} onClick={() => setShowAll(!showAll)}>
                            {showAll ? "See Less..." : "See More..."}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const sortOrderOptions: IDropdownOption[] = [
    { key: "newest", text: "Newest first" },
    { key: "oldest", text: "Oldest first" }
];

export interface ClinicalFilters {
    patId: string;
    patientName: string;
    noteTypes: string[];
    formNames: string[]; // AssessmentSource
    serviceCodes: string[]; // Raw service codes; hidden when placeholder-only.
    clinicalActivities: string[];
    assessmentScales: string[];
    servicePrograms: string[];
    serviceDescriptions: string[]; // Human-readable service type.
    providers: string[]; // MedicalProvider
    caseManagers: string[]; // PrimaryCaseManager
    assessmentStaff: string[];
    dateRanges: string[]; // ISO date strings for quarter start
    consumers: string[]; // ConsumerID filter (patient MRN/ID)
    topResults: number; // 0 = all
    sortOrder: "newest" | "oldest";
    severityIndicators: string[];
}

export interface FilterOptions {
    noteTypes: IDropdownOption[];
    formNames: IDropdownOption[];
    serviceCodes: IDropdownOption[];
    clinicalActivities: IDropdownOption[];
    assessmentScales: IDropdownOption[];
    servicePrograms: IDropdownOption[];
    serviceDescriptions: IDropdownOption[];
    providers: IDropdownOption[];
    caseManagers: IDropdownOption[];
    assessmentStaff: IDropdownOption[];
    dateRanges: IDropdownOption[];
    consumers: IDropdownOption[];
    severityIndicators: IDropdownOption[];
}

interface ClinicalSettingsProps {
    filters: ClinicalFilters;
    onFiltersChange: (filters: ClinicalFilters) => void;
}

type FilterOption = { key: string; text: string };

export const ClinicalSettings = ({ filters, onFiltersChange }: ClinicalSettingsProps) => {
    const { t } = useTranslation();
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = accounts.length > 0 && inProgress === "none";
    const client = isAuthenticated ? instance : undefined;
    const account = accounts[0];
    // Store initial unfiltered options to preserve all choices
    const [initialFilterOptions, setInitialFilterOptions] = useState<FilterOptions | null>(null);

    // Get cached data from context
    const {
        filterOptions,
        setFilterOptions,
        caseloadSelection,
        facetCounts,
        setFacetCounts,
        totalNotesAvailable,
        setTotalNotesAvailable,
        filteredNotesCount,
        setFilteredNotesCount
    } = useClinicalFilters();

    // Multi-patient support: derive patient IDs from caseload selection
    const caseloadPatientIds = !SHOW_SUMMARIES && caseloadSelection.selectedPatients.length > 0
        ? caseloadSelection.selectedPatients.map(p => p.consumerId)
        : [];

    // Build ID→name lookup from teamPatients for consumer facet display
    const idToName = useMemo(() => {
        const map = new Map<string, string>();
        caseloadSelection.teamPatients.forEach(p => map.set(p.consumerId, p.consumer));
        return map;
    }, [caseloadSelection.teamPatients]);

    // Convert ConsumerID facet entries to options with patient names
    const toConsumerOptions = (items: { value: string; count: number }[]): IDropdownOption[] =>
        (items || []).map(i => {
            const name = idToName.get(i.value) || i.value;
            return { key: i.value, text: `${name} (${i.count})` };
        });

    const [isLoadingFilters, setIsLoadingFilters] = useState(false);

    // Helper to get auth headers
    const getAuthHeaders = async (): Promise<HeadersInit> => {
        if (client && account) {
            client.setActiveAccount(account);
            const token = await getToken(client);
            return token ? { Authorization: `Bearer ${token}` } : {};
        }
        return {};
    };

    const loadFacetCounts = async (patId: string, patientIds: string[] = [], forceRefresh: boolean = false) => {
        // Check sessionStorage cache first (cache for 5 minutes)
        const cacheKey = patientIds.length > 0 ? `facets_multi_${patientIds.sort().join(",")}` : `facets_${patId}`;
        const cached = sessionStorage.getItem(cacheKey);
        if (cached && !forceRefresh) {
            try {
                const { data, timestamp } = JSON.parse(cached);
                const age = Date.now() - timestamp;
                if (age < 5 * 60 * 1000) {
                    // 5 minutes
                    console.log("Using cached facets for", cacheKey, "age:", Math.round(age / 1000), "s");
                    applyFacetData(data);
                    return;
                }
            } catch (e) {
                console.warn("Failed to parse cached facets", e);
            }
        }

        setIsLoadingFilters(true);

        try {
            const headers = await getAuthHeaders();
            const params = new URLSearchParams();
            if (patientIds.length > 0) {
                params.set("patientIds", patientIds.join(","));
            } else {
                params.set("patId", patId);
            }
            const response = await fetch(`/patient-facets?${params.toString()}`, { headers });

            if (response.ok) {
                const data = await response.json();
                console.log("loadFacetCounts response:", { totalCount: data.totalCount, facets: Object.keys(data.facets) });

                // Cache the response
                sessionStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));

                applyFacetData(data);
            }
        } finally {
            setIsLoadingFilters(false);
        }
    };

    const applyFacetData = (data: any) => {
        setFacetCounts(data);
        const facets = data.facets ?? {};

        // Set totalNotesAvailable on first load for this patient
        // (only when we have no active filters, i.e., initial load)
        const hasNoFilters =
            filters.noteTypes.length === 0 &&
            filters.formNames.length === 0 &&
            filters.serviceCodes.length === 0 &&
            filters.clinicalActivities.length === 0 &&
            filters.assessmentScales.length === 0 &&
            filters.servicePrograms.length === 0 &&
            filters.serviceDescriptions.length === 0 &&
            filters.providers.length === 0 &&
            filters.caseManagers.length === 0 &&
            filters.assessmentStaff.length === 0 &&
            filters.consumers.length === 0 &&
            filters.dateRanges.length === 0 &&
            filters.severityIndicators.length === 0;

        console.log("loadFacetCounts response:", {
            totalCount: data.totalCount,
            hasNoFilters,
            currentTotalNotesAvailable: totalNotesAvailable,
            willSetTotal: hasNoFilters
        });

        if (hasNoFilters) {
            console.log("Setting totalNotesAvailable to:", data.totalCount);
            setTotalNotesAvailable(data.totalCount ?? 0);
        }
        // Convert facets to dropdown options with counts
        const toOptions = (items: { value: string; display?: string; count: number }[]): IDropdownOption[] =>
            (items || []).map(i => ({ key: i.value, text: `${i.display || i.value} (${i.count})` }));

        setFilterOptions({
            noteTypes: toOptions(facets.NoteType),
            formNames: toOptions(facets.AssessmentSource),
            serviceCodes: toOptions(facets.ServiceCode),
            clinicalActivities: toOptions(facets.ClinicalActivity),
            assessmentScales: toOptions(facets.AssessmentScale),
            servicePrograms: toOptions(facets.ServiceProgram),
            serviceDescriptions: toOptions(facets.ServiceDescription),
            providers: toOptions(facets.MedicalProvider),
            caseManagers: toOptions(facets.PrimaryCaseManager),
            assessmentStaff: toOptions(facets.AssessmentStaff),
            dateRanges: toOptions(facets.AssessmentDate),
            consumers: toConsumerOptions(facets.ConsumerID),
            severityIndicators:
                toOptions(facets.SeverityIndicator)?.map(opt =>
                    opt.key === "" || opt.key === null ? { ...opt, key: "", text: opt.text.replace("(", "Unknown (") } : opt
                ) || []
        });
        // Save initial options (only on first load for this patient)
        if (!initialFilterOptions) {
            setInitialFilterOptions({
                noteTypes: toOptions(facets.NoteType),
                formNames: toOptions(facets.AssessmentSource),
                serviceCodes: toOptions(facets.ServiceCode),
                clinicalActivities: toOptions(facets.ClinicalActivity),
                assessmentScales: toOptions(facets.AssessmentScale),
                servicePrograms: toOptions(facets.ServiceProgram),
                serviceDescriptions: toOptions(facets.ServiceDescription),
                providers: toOptions(facets.MedicalProvider),
                caseManagers: toOptions(facets.PrimaryCaseManager),
                assessmentStaff: toOptions(facets.AssessmentStaff),
                dateRanges: toOptions(facets.AssessmentDate),
                consumers: toConsumerOptions(facets.ConsumerID),
                severityIndicators: toOptions(facets.SeverityIndicator) || []
            });
        }
    };

    const patientSelected = !!filters.patId || caseloadPatientIds.length > 0;

    useEffect(() => {
        if (caseloadPatientIds.length > 0) {
            loadFacetCounts("", caseloadPatientIds);
        } else if (filters.patId) {
            loadFacetCounts(filters.patId);
        }
    }, [filters.patId, caseloadPatientIds.length]);

    // Re-fetch filtered facets when any filter selection changes
    useEffect(() => {
        if (!filters.patId && caseloadPatientIds.length === 0) return;

        // Check if any filter is active
        const hasActiveFilters =
            filters.noteTypes.length > 0 ||
            filters.formNames.length > 0 ||
            filters.serviceCodes.length > 0 ||
            filters.clinicalActivities.length > 0 ||
            filters.assessmentScales.length > 0 ||
            filters.servicePrograms.length > 0 ||
            filters.serviceDescriptions.length > 0 ||
            filters.providers.length > 0 ||
            filters.caseManagers.length > 0 ||
            filters.assessmentStaff.length > 0 ||
            filters.dateRanges.length > 0 ||
            filters.consumers.length > 0 ||
            filters.severityIndicators.length > 0;

        // If no active filters, reset to initial state
        if (!hasActiveFilters) {
            loadFacetCounts(filters.patId, caseloadPatientIds);
            setFilteredNotesCount(0); // Reset to show totalNotesAvailable
            return;
        }

        const fetchFilteredFacets = async () => {
            const params = new URLSearchParams();
            if (caseloadPatientIds.length > 0) {
                params.set("patientIds", caseloadPatientIds.join(","));
            } else {
                params.set("patId", filters.patId);
            }
            if (filters.noteTypes.length) params.append("noteTypes", filters.noteTypes.join(","));
            if (filters.formNames.length) params.append("formNames", filters.formNames.join(","));
            if (filters.serviceCodes.length) params.append("serviceCodes", filters.serviceCodes.join(","));
            if (filters.clinicalActivities.length) params.append("clinicalActivities", filters.clinicalActivities.join(","));
            if (filters.assessmentScales.length) params.append("assessmentScales", filters.assessmentScales.join(","));
            if (filters.servicePrograms.length) params.append("servicePrograms", filters.servicePrograms.join(","));
            if (filters.serviceDescriptions.length) params.append("serviceDescriptions", filters.serviceDescriptions.join(","));
            if (filters.providers.length) params.append("providers", filters.providers.join(","));
            if (filters.caseManagers.length) params.append("caseManagers", filters.caseManagers.join(","));
            if (filters.assessmentStaff.length) params.append("assessmentStaff", filters.assessmentStaff.join(","));
            if (filters.consumers.length) params.append("consumers", filters.consumers.join(","));
            if (filters.dateRanges.length) params.append("dateYears", filters.dateRanges.join(","));
            if (filters.severityIndicators.length) params.append("severityIndicators", filters.severityIndicators.join(",")); // ADD

            try {
                console.log("fetchFilteredFacets starting, hasActiveFilters:", hasActiveFilters);
                const headers = await getAuthHeaders();
                console.log("fetchFilteredFacets got headers:", Object.keys(headers));
                const response = await fetch(`/patient-facets?${params.toString()}`, { headers });
                console.log("fetchFilteredFacets response status:", response.status);
                if (response.ok) {
                    const data = await response.json();
                    const facets = data.facets ?? {};
                    console.log("fetchFilteredFacets got data, totalCount:", data.totalCount);
                    setFacetCounts(data);
                    setFilteredNotesCount(data.totalCount ?? 0);

                    // Update filter options with new counts from filtered results
                    const toOptions = (facetList: any[]) => facetList?.map(f => ({ key: f.value, text: `${f.value} (${f.count})` })) || [];

                    // Merge: keep all initial options, update counts from filtered results
                    const mergeOptions = (
                        initial: IDropdownOption[],
                        facets: { value: string | number; display?: string; count: number }[] | undefined
                    ): FilterOption[] => {
                        if (!facets) return initial.map(o => ({ key: String(o.key), text: o.text }));

                        return facets.map(f => ({
                            key: String(f.value),
                            text: `${f.display || f.value} (${f.count})`
                        }));
                    };

                    if (initialFilterOptions) {
                        setFilterOptions({
                            noteTypes: mergeOptions(initialFilterOptions.noteTypes, facets.NoteType),
                            formNames: mergeOptions(initialFilterOptions.formNames, facets.AssessmentSource),
                            serviceCodes: mergeOptions(initialFilterOptions.serviceCodes, facets.ServiceCode),
                            clinicalActivities: mergeOptions(initialFilterOptions.clinicalActivities, facets.ClinicalActivity),
                            assessmentScales: mergeOptions(initialFilterOptions.assessmentScales, facets.AssessmentScale),
                            servicePrograms: mergeOptions(initialFilterOptions.servicePrograms, facets.ServiceProgram),
                            serviceDescriptions: mergeOptions(initialFilterOptions.serviceDescriptions, facets.ServiceDescription),
                            providers: mergeOptions(initialFilterOptions.providers, facets.MedicalProvider),
                            caseManagers: mergeOptions(initialFilterOptions.caseManagers, facets.PrimaryCaseManager),
                            assessmentStaff: mergeOptions(initialFilterOptions.assessmentStaff, facets.AssessmentStaff),
                            dateRanges: mergeOptions(initialFilterOptions.dateRanges, facets.AssessmentDate),
                            consumers: (() => {
                                // Merge: keep all initial consumer options, update counts from filtered results
                                const updated = toConsumerOptions(facets.ConsumerID);
                                if (!initialFilterOptions.consumers?.length) return updated;
                                const updatedKeys = new Set(updated.map(o => String(o.key)));
                                const missing = initialFilterOptions.consumers
                                    .filter(o => !updatedKeys.has(String(o.key)))
                                    .map(o => {
                                        const name = idToName.get(String(o.key)) || String(o.key);
                                        return { key: o.key, text: `${name} (0)` };
                                    });
                                return [...updated, ...missing];
                            })(),
                            severityIndicators: mergeOptions(initialFilterOptions.severityIndicators, facets.SeverityIndicator)
                        });
                    }
                } else {
                    console.error("fetchFilteredFacets failed with status:", response.status);
                }
            } catch (error) {
                console.error("Error fetching filtered facets:", error);
            }
        };
        fetchFilteredFacets();
    }, [
        filters.patId,
        filters.noteTypes,
        filters.formNames,
        filters.serviceCodes,
        filters.clinicalActivities,
        filters.assessmentScales,
        filters.servicePrograms,
        filters.serviceDescriptions,
        filters.providers,
        filters.caseManagers,
        filters.assessmentStaff,
        filters.dateRanges,
        filters.consumers,
        filters.severityIndicators
    ]);

    const hasOptions = (options?: IDropdownOption[]) => (options ?? []).length > 0;

    return (
        <Stack tokens={{ childrenGap: 8 }} className={styles.container}>
            {/* Notes count */}
            {patientSelected && (
                <div className={styles.notesCount}>
                    {filteredNotesCount || totalNotesAvailable} of {totalNotesAvailable} Available Notes
                </div>
            )}

            {/* Facet sections - only show when patient selected */}
            {patientSelected && filterOptions && (
                <>
                    {!SHOW_SUMMARIES && filterOptions.consumers?.length > 0 && (
                        <FacetSection
                            title="Patients"
                            options={filterOptions.consumers}
                            selectedKeys={filters.consumers}
                            onChange={newSelection => onFiltersChange({ ...filters, consumers: newSelection })}
                            maxVisible={20}
                        />
                    )}
                    {hasOptions(filterOptions.formNames) && (
                        <FacetSection
                            title="Form Names"
                            options={filterOptions.formNames}
                            selectedKeys={filters.formNames}
                            onChange={newSelection => onFiltersChange({ ...filters, formNames: newSelection })}
                        />
                    )}
                    {hasOptions(filterOptions.noteTypes) && (
                        <FacetSection
                            title="Note Types"
                            options={filterOptions.noteTypes}
                            selectedKeys={filters.noteTypes}
                            onChange={newSelection => {
                                console.log("noteTypes onChange called:", newSelection, "current filters:", filters);
                                onFiltersChange({ ...filters, noteTypes: newSelection });
                            }}
                        />
                    )}
                    {hasOptions(filterOptions.clinicalActivities) && (
                        <FacetSection
                            title="Clinical Activity"
                            options={filterOptions.clinicalActivities}
                            selectedKeys={filters.clinicalActivities}
                            onChange={newSelection => onFiltersChange({ ...filters, clinicalActivities: newSelection })}
                        />
                    )}
                    {hasOptions(filterOptions.assessmentScales) && (
                        <FacetSection
                            title="Assessment Scale"
                            options={filterOptions.assessmentScales}
                            selectedKeys={filters.assessmentScales}
                            onChange={newSelection => onFiltersChange({ ...filters, assessmentScales: newSelection })}
                        />
                    )}
                    {hasOptions(filterOptions.serviceDescriptions) && (
                        <FacetSection
                            title="Service Type"
                            options={filterOptions.serviceDescriptions}
                            selectedKeys={filters.serviceDescriptions}
                            onChange={newSelection => onFiltersChange({ ...filters, serviceDescriptions: newSelection })}
                        />
                    )}
                    {hasOptions(filterOptions.serviceCodes) && (
                        <FacetSection
                            title="Service Codes"
                            options={filterOptions.serviceCodes}
                            selectedKeys={filters.serviceCodes}
                            onChange={newSelection => onFiltersChange({ ...filters, serviceCodes: newSelection })}
                        />
                    )}
                    {hasOptions(filterOptions.severityIndicators) && (
                        <FacetSection
                            title="Severity"
                            options={filterOptions.severityIndicators}
                            selectedKeys={filters.severityIndicators}
                            onChange={newSelection => onFiltersChange({ ...filters, severityIndicators: newSelection })}
                        />
                    )}
                    {hasOptions(filterOptions.dateRanges) && (
                        <FacetSection
                            title="Created Date"
                            options={filterOptions.dateRanges}
                            selectedKeys={filters.dateRanges}
                            onChange={newSelection => onFiltersChange({ ...filters, dateRanges: newSelection })}
                        />
                    )}

                    {/* Add more facet sections as needed */}
                </>
            )}

            {isLoadingFilters && <Spinner label="Loading..." size={1} />}
        </Stack>
    );
};
